import React from "react";
import { reportClientError } from "@/lib/api";

const DOM_CONFLICT = /removeChild|insertBefore|not a child of this node/i;

/**
 * Evita la pantalla en blanco: si algo falla al dibujar la interfaz, muestra el error (y lo informa al
 * servidor) en lugar de dejar la página vacía.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        reportClientError({
            message: `${error?.name || "Error"}: ${error?.message || error}`,
            stack: error?.stack,
            componentStack: info?.componentStack,
            kind: "react-boundary",
        });
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        const domConflict = DOM_CONFLICT.test(String(error?.message));
        return (
            <div className="min-h-screen bg-brand-50 flex items-center justify-center p-6" translate="no">
                <div data-testid="error-boundary" className="w-full max-w-lg bg-white border border-brand-300 shadow-xl p-6 space-y-3">
                    <h1 className="font-headings font-bold text-brand-950 text-lg">Algo falló en la pantalla</h1>
                    {domConflict ? (
                        <p className="text-sm text-brand-800">
                            Parece que el navegador o una extensión está modificando la página (por ejemplo, la traducción
                            automática). Desactívala para este sitio y recarga.
                        </p>
                    ) : (
                        <p className="text-sm text-brand-800">Puedes recargar la página. Tus plantillas guardadas no se pierden.</p>
                    )}
                    <pre className="bg-brand-950 text-brand-100 p-3 font-mono text-[11px] overflow-auto whitespace-pre-wrap break-words">
                        {String(error?.message || error)}
                    </pre>
                    <p className="text-[11px] text-brand-600">El error ya se informó al servidor.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-brand-900 text-white text-sm font-medium hover:bg-brand-800"
                    >
                        Recargar la página
                    </button>
                </div>
            </div>
        );
    }
}
